import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getLiveSessionReports,
  LiveSessionReportsDetail,
} from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LiveAccessUserLivestreamDetail: React.FC = () => {
  const { userId, sessionId } = useParams<{ userId: string; sessionId: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<LiveSessionReportsDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await getLiveSessionReports(sessionId);
      setDetails(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load livestream details');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button
          onClick={() => navigate(`/live-access/${userId}/livestreams`)}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Livestreams
        </button>

        {loading ? (
          <div className='flex items-center justify-center py-24'>
            <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
          </div>
        ) : !details?.session ? (
          <div className='rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm'>
            <p className='text-lg font-semibold text-gray-900'>Livestream details not found</p>
          </div>
        ) : (
          <>
            <div className='mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
              <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
                <div className='flex gap-4'>
                  {details.session.CoverUrl ? (
                    <img
                      src={details.session.CoverUrl}
                      alt={details.session.Title || details.session.SessionID}
                      className='h-24 w-24 rounded-xl object-cover'
                    />
                  ) : (
                    <div className='flex h-24 w-24 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-lg font-bold text-white'>
                      LIVE
                    </div>
                  )}
                  <div>
                    <h1 className='text-2xl font-bold text-gray-900'>{details.session.Title || 'Untitled Livestream'}</h1>
                    <p className='mt-1 text-sm text-gray-500'>
                      Host: {details.host?.Name || details.host?.UserName || details.session.HostUserID}
                    </p>
                    <p className='mt-1 text-xs font-mono text-gray-400'>{details.session.SessionID}</p>
                    <div className='mt-3 flex flex-wrap gap-2'>
                      <span className='inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                        {details.session.LifecycleStatus}
                      </span>
                      {details.session.PublishStatus && (
                        <span className='inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700'>
                          {details.session.PublishStatus}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                  <div className='rounded-xl bg-gray-50 px-4 py-3 text-center'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Reports</p>
                    <p className='mt-1 text-2xl font-bold text-rose-600'>{details.reportCount}</p>
                  </div>
                  <div className='rounded-xl bg-gray-50 px-4 py-3 text-center'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Reporters</p>
                    <p className='mt-1 text-2xl font-bold text-blue-600'>{details.reporterCount}</p>
                  </div>
                  <div className='rounded-xl bg-gray-50 px-4 py-3 text-center'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Last Report</p>
                    <p className='mt-1 text-sm font-semibold text-gray-900'>{formatDate(details.lastReportedAt)}</p>
                  </div>
                </div>
              </div>

              <div className='mt-5 grid gap-4 border-t border-gray-100 pt-5 sm:grid-cols-2 lg:grid-cols-4'>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Category</p>
                  <p className='mt-1 text-sm text-gray-700'>{details.session.Category || 'N/A'}</p>
                </div>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Content Rating</p>
                  <p className='mt-1 text-sm text-gray-700'>{details.session.ContentRating || 'N/A'}</p>
                </div>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Started</p>
                  <p className='mt-1 text-sm text-gray-700'>{formatDate(details.session.StartedAt)}</p>
                </div>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Ended</p>
                  <p className='mt-1 text-sm text-gray-700'>{formatDate(details.session.EndedAt)}</p>
                </div>
              </div>
            </div>

            <div className='rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
              <div className='border-b border-gray-200 px-6 py-4'>
                <h2 className='text-lg font-semibold text-gray-900'>Live Reports</h2>
              </div>

              {details.reports.length === 0 ? (
                <div className='px-6 py-16 text-center'>
                  <p className='text-lg font-semibold text-gray-900'>No reports on this livestream</p>
                  <p className='mt-2 text-sm text-gray-500'>The session exists, but there are no participant reports yet.</p>
                </div>
              ) : (
                <div className='divide-y divide-gray-200'>
                  {details.reports.map((report) => (
                    <div key={report.ReportID || `${report.ReporterUserID}-${report.createdAt}`} className='px-6 py-5'>
                      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
                        <div className='space-y-3'>
                          <div>
                            <div className='flex flex-wrap items-center gap-2'>
                              <span className='inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700'>
                                {report.ReasonLabel}
                              </span>
                              <span className='inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600'>
                                {report.ReasonCode}
                              </span>
                              {report.BlockHost && (
                                <span className='inline-flex rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white'>
                                  Reporter blocked host
                                </span>
                              )}
                            </div>
                            <p className='mt-2 text-sm text-gray-600'>
                              Reported by <span className='font-semibold text-gray-900'>{report.ReporterUserName || report.ReporterUserID}</span>
                            </p>
                            <p className='mt-1 text-xs font-mono text-gray-400'>{report.ReporterUserID}</p>
                          </div>

                          <div>
                            <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Details</p>
                            <p className='mt-1 text-sm text-gray-700'>{report.Details || 'No additional details provided.'}</p>
                          </div>

                          {report.ScreenshotUrls.length > 0 && (
                            <div>
                              <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Screenshots</p>
                              <div className='mt-2 flex flex-wrap gap-3'>
                                {report.ScreenshotUrls.map((url, index) => (
                                  <a
                                    key={`${report.ReportID || report.ReporterUserID}-${index}`}
                                    href={url}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 hover:border-[#FF9C15]'
                                  >
                                    <img src={url} alt={`Report screenshot ${index + 1}`} className='h-24 w-24 object-cover transition-transform group-hover:scale-105' />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className='shrink-0 text-sm text-gray-500'>
                          <p className='font-semibold text-gray-800'>{formatDate(report.createdAt)}</p>
                          {report.ReportID && <p className='mt-1 font-mono text-xs text-gray-400'>{report.ReportID}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveAccessUserLivestreamDetail;
